import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	DevOpsEnvironmentCategory,
	deploymentData_softwareReleasesVersionDetailIssueList$key,
} from '@atlassian/jira-relay/src/__generated__/deploymentData_softwareReleasesVersionDetailIssueList.graphql';
import type { DeploymentState } from '@atlassian/jira-relay/src/__generated__/statusIcon_softwareReleasesVersionDetailIssueList.graphql.ts';
import { DeploymentStateOptions } from '@atlassian/jira-software-deployments-icons-common/src/ui/types.tsx';
import { VIEWPORT_WIDTH_LARGE } from '../../../../../common/constants.tsx';
import messages from './messages.tsx';
import { StatusIcon } from './status-icon/index.tsx';

type FormatMessage = IntlShape['formatMessage'];
type Props = {
	onDeploymentClicked: () => void;
	jiraIssueDevOpsSummarisedEntities: deploymentData_softwareReleasesVersionDetailIssueList$key;
};

const mapEnvironmentCategory = (
	category: DevOpsEnvironmentCategory,
	formatMessage: FormatMessage,
): string => {
	switch (category) {
		case 'PRODUCTION':
			return formatMessage(messages.production);
		case 'STAGING':
			return formatMessage(messages.staging);
		case 'TESTING':
			return formatMessage(messages.testing);
		case 'DEVELOPMENT':
			return formatMessage(messages.development);
		case 'UNMAPPED':
			return formatMessage(messages.unmapped);
		default:
			return formatMessage(messages.unmapped);
	}
};

const mapState = (state: DeploymentState, formatMessage: FormatMessage): string => {
	switch (state) {
		case DeploymentStateOptions.PENDING:
			return formatMessage(messages.pending);
		case DeploymentStateOptions.IN_PROGRESS:
			return formatMessage(messages.inProgress);
		case DeploymentStateOptions.SUCCESSFUL:
			return formatMessage(messages.successful);
		case DeploymentStateOptions.FAILED:
			return formatMessage(messages.failed);
		case DeploymentStateOptions.ROLLED_BACK:
			return formatMessage(messages.rolledBack);
		case DeploymentStateOptions.CANCELLED:
			return formatMessage(messages.cancelled);
		default:
			return formatMessage(messages.unknown);
	}
};

export const DeploymentData = ({
	onDeploymentClicked,
	jiraIssueDevOpsSummarisedEntities: jiraIssueSummarisedEntities,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { summarisedDeployments } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment deploymentData_softwareReleasesVersionDetailIssueList on DevOpsSummarisedEntities {
				summarisedDeployments {
					deploymentState
					deploymentEnvironment {
						category
					}
					...statusIcon_softwareReleasesVersionDetailIssueList
				}
			}
		`,
		jiraIssueSummarisedEntities,
	);

	// If null, there is nothing to display
	if (summarisedDeployments == null) return null;
	// Same logic as <StatusIcon/> component that falls back to Unknown if null
	const category = summarisedDeployments.deploymentEnvironment?.category
		? summarisedDeployments.deploymentEnvironment.category
		: 'UNMAPPED';
	const state = summarisedDeployments.deploymentState
		? summarisedDeployments.deploymentState
		: 'UNKNOWN';
	const environmentCategory = mapEnvironmentCategory(category, formatMessage);
	const deploymentState = mapState(state, formatMessage);

	return (
		<BaseContainer>
			<Button
				spacing="none"
				appearance="subtle-link"
				onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
					onDeploymentClicked();
					fireUIAnalytics(analyticsEvent, 'deploymentSummaryCell');
				}}
				aria-label={formatMessage(messages.deploymentDataAriaLabel, {
					environmentCategory,
					deploymentState,
				})}
			>
				<ContentsContainer>
					<StatusContainer>
						<StatusIcon jiraSummarisedDeployments={summarisedDeployments} />
					</StatusContainer>
					<DescriptionInternalContainer>{environmentCategory}</DescriptionInternalContainer>
				</ContentsContainer>
			</Button>
		</BaseContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BaseContainer = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionInternalContainer = styled.div({
	marginLeft: token('space.100'),
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_LARGE}px)`]: {
		display: 'block',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_LARGE}px)`]: {
		display: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusContainer = styled.div({
	height: '24px',

	color: token('color.text.subtlest'),
});
